/// <amd-module name="Repos/Changes/Components/ChangesViewer" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { ChangesContext, IChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { DiffViewerType, IChange, IChangeCollection, IChangesFilesService } from "Repos/ChangesCommon/Contracts";
import {
    IDiffIntegration,
    IDiffMonacoDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    IDiscussionIntegrationService
} from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { IPerformanceScenarioPendingWork, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { css, IVssComponentContext, usePageContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Observer } from "VSSUI/Observer";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { ChangedFileViewMode } from "../Contracts";
import { getChangesForPath } from "../Util";
import { ChangeItemViewer } from "./ChangeItemViewer";
import { ChangesSummaryViewer } from "./ChangesSummaryViewer";
import { getFilesHubViewService } from "Repos/Views/FilesHub/FilesHubViewService";
import { Uri } from "VSS/Platform/Util/Url";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { IEditorSelection } from "Repos/Common/Contracts/Editor";

export interface IChangesViewerProps<TVersionDescriptor> {
    /**
     * If true, keyboard shortcuts for next and previous difference will be added to Monaco
     */
    addDiffKeyboardShortcuts: boolean;

    /**
     * Collection of changes being viewed
     */
    changes: IChangeCollection<TVersionDescriptor>;

    /**
     * Optional CSS class name for the root container element
     */
    className?: string;

    /**
     * Service for interacting with discussion threads about the changes being viewed.
     */
    discussionService?: IDiscussionIntegrationService;

    /**
     * View mode when a file is selected
     */
    fileViewMode: IReadonlyObservableValue<ChangedFileViewMode>;

    /**
     * Text to highlight in the file headers (search text)
     */
    highlightText?: string;

    /**
     * Called when "View" button is clicked in the change's summary header
     */
    onViewFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, change: IChange) => void;

    /**
     * Callback to get the url to file in pull request files tab
     */
    getFileUrl?: (path: string) => string;

    /**
     * If set, scroll to the specified discussion thread.
     */
    selectedThreadId?: IReadonlyObservableValue<number | undefined>;

    /**
     * The currently selected change
     */
    selectedItem: IReadonlyObservableValue<IChange>;

    /**
     * Widgets to include in the monaco editor/diff viewer in addition to the discussion-related widgets
     */
    viewerWidgets?: IReadonlyObservableArray<IEditorWidgetRegistration> | IEditorWidgetRegistration[];

    /**
     * If true, the ZeroData text in the viewer will be appropriate
     */
    isMergeCommit?: boolean;

    /**
     * If true, the selected iteration has ResolveConflicts reason meaning that it was created after resolving conflicts using Merge Conflict Resolution API
     */
    isConflictResolutionIteration?: boolean;

    /**
     * A link to the page to preview merge changes. Shows as a "View merge changes" button for conflict resolution iteration.
     * It doesn't have any effect if isConflictResolutionIteration isn't true.
     */
    conflictResolutionIterationLink?: string;

    /**
     * If true, the ZeroData text in the viewer will be appropriate
     */
    isChangeset?: boolean;

    commitTooManyChanges?: boolean;
}

export class ChangesViewer<TVersionDescriptor> extends VssComponent<IChangesViewerProps<TVersionDescriptor>> {
    private containerRef?: HTMLDivElement;
    private diffIntegration: IDiffMonacoDiscussionIntegration | undefined;
    private diffWidth = new ObservableValue<number | undefined>(undefined);

    constructor(props: IChangesViewerProps<TVersionDescriptor>, context: IVssComponentContext) {
        super(props, context);

        this.observe([{ propertyName: "selectedThreadId", observer: this.revealThreadId }]);
    }

    public componentDidMount() {
        window.addEventListener("resize", this.onResize);
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.onResize);
    }

    public render() {
        const {
            addDiffKeyboardShortcuts,
            changes,
            className,
            discussionService,
            fileViewMode,
            getFileUrl,
            highlightText,
            onViewFile,
            selectedItem,
            selectedThreadId,
            viewerWidgets,
            isMergeCommit,
            commitTooManyChanges,
            isConflictResolutionIteration,
            conflictResolutionIterationLink,
            isChangeset
        } = this.props;

        return (
            <ChangesContext.Consumer>
                {(changesContext: IChangesContext<TVersionDescriptor>) => (
                    <Observer selectedItem={selectedItem}>
                        {(selectionProps: { selectedItem: IChange }) => {
                            const { filesService, viewService } = changesContext;
                            const itemPath = selectionProps.selectedItem.path;

                            const getCodeSelectionLinkUrl = (selection: IEditorSelection, editorType?: number, diffOrientation?: DiffViewerOrientation): string => {
                                const filesService = getFilesHubViewService(this.context.pageContext);
                                console.log(selectedItem);
                                const url = new Uri(filesService.getItemUrl(itemPath, { absoluteUrl: true }));
                                url.addQueryParams({
                                    line: selection.startLineNumber,
                                    lineEnd: selection.endLineNumber,
                                    lineStartColumn: selection.startColumn,
                                    lineEndColumn: selection.endColumn,
                                    type: editorType,
                                    orientation: diffOrientation,
                                    lineStyle: "plain",
                                    _a: "files"
                                });
                        
                                return url.absoluteUri;
                            };

                            let viewer;

                            if (selectionProps.selectedItem.isFolder) {
                                const folderItems = getChangesForPath(changes, itemPath);
                                if (folderItems.length > 0) {
                                    const parentVersion = filesService.convertDescriptorToVersion(changes.targetVersion);

                                    let rootDiffIntegration: IDiffSummaryDiscussionIntegration | undefined;
                                    if (discussionService && viewService && viewService.isRootItem(selectionProps.selectedItem)) {
                                        rootDiffIntegration = discussionService.getRootDiffSummaryIntegration();
                                    }

                                    viewer = (
                                        <ChangesWithDiscussion
                                            discussionService={discussionService}
                                            filesService={filesService}
                                            paths={folderItems.map(c => c.path)}
                                            renderChanges={(diffIntegrations?: IDiffSummaryDiscussionIntegration[]) => (
                                                <ChangesSummaryViewer
                                                    changes={folderItems}
                                                    compareVersion={parentVersion}
                                                    diffIntegrations={diffIntegrations}
                                                    folderPath={itemPath}
                                                    getFileUrl={getFileUrl}
                                                    highlightText={highlightText}
                                                    onViewFile={onViewFile}
                                                    rootDiffIntegration={rootDiffIntegration}
                                                />
                                            )}
                                            viewerType={"summary"}
                                            getCodeSelectionLinkUrl={getCodeSelectionLinkUrl}
                                        />
                                    );
                                } else {
                                    const image = getAssetLocation(this.context.pageContext, "ms.vss-code-web/changes/Content/zero-data.svg");
                                    const zeroDataText = isMergeCommit ? Resources.NoUniqueChangeInMergeCommit :
                                                         commitTooManyChanges ? Resources.CommitTooManyChanges :
                                                         isChangeset ? Resources.NoUniqueChangeInChangeset :
                                                         isConflictResolutionIteration ? Resources.ConflictResolutionIteration :
                                                         Resources.NoUniqueChangeInCommit;

                                    let actionButtonProps = {};
                                    if (isConflictResolutionIteration && conflictResolutionIterationLink) {
                                        actionButtonProps = {
                                            iconProps: { iconName: "BranchCommit" }
                                        };
                                    }

                                    viewer = <ZeroData
                                        imagePath={image}
                                        imageAltText={zeroDataText}
                                        secondaryText={zeroDataText}
                                        actionType={isConflictResolutionIteration ? ZeroDataActionType.ctaButton : undefined}
                                        actionText={isConflictResolutionIteration ? Resources.ViewMergeChanges : undefined}
                                        actionHref={isConflictResolutionIteration ? conflictResolutionIterationLink : undefined}
                                        actionButtonProps={actionButtonProps} />;
                                }
                            } else {
                                viewer = (
                                    <ChangesWithDiscussion
                                        discussionService={discussionService}
                                        filesService={changesContext.filesService}
                                        paths={[itemPath]}
                                        renderChanges={(diffIntegrations?: IDiffMonacoDiscussionIntegration[]) => {
                                            this.diffIntegration = diffIntegrations ? diffIntegrations[0] : undefined;
                                            if (this.diffIntegration && selectedThreadId && selectedThreadId.value) {
                                                this.diffIntegration.revealThreadId(selectedThreadId.value);
                                            }

                                            if (this.diffIntegration) {
                                                this.diffIntegration.setDiffWidth(this.diffWidth);
                                            }

                                            let diffWidgets: ObservableCollection<IEditorWidgetRegistration> | undefined;
                                            if (this.diffIntegration || viewerWidgets) {
                                                diffWidgets = new ObservableCollection<IEditorWidgetRegistration>();
                                                if (this.diffIntegration) {
                                                    diffWidgets.push(this.diffIntegration.getWidgets());
                                                }
                                                if (viewerWidgets) {
                                                    diffWidgets.push(viewerWidgets);
                                                }
                                            }

                                            return (
                                                <ChangeItemViewer
                                                    addDiffKeyboardShortcuts={addDiffKeyboardShortcuts}
                                                    compareVersion={changes.targetVersion}
                                                    diffWidgets={diffWidgets}
                                                    fileViewMode={fileViewMode}
                                                    selectedItem={selectionProps.selectedItem}
                                                />
                                            );
                                        }}
                                        viewerType={"monaco"}
                                        getCodeSelectionLinkUrl={getCodeSelectionLinkUrl}
                                    />
                                );
                            }

                            return (
                                <div
                                    className={css(
                                        className,
                                        "repos-changes-viewer flex-column rhythm-vertical-16 scroll-auto scroll-auto-hide",
                                        selectionProps.selectedItem.isFolder && "custom-scrollbar is-folder"
                                    )}
                                    ref={this.setContainerRef}
                                >
                                    {viewer}
                                </div>
                            );
                        }}
                    </Observer>
                )}
            </ChangesContext.Consumer>
        );
    }

    private onResize = () => {
        if (this.containerRef && this.diffWidth && this.diffWidth.value !== this.containerRef.clientWidth) {
            this.diffWidth.value = this.containerRef.clientWidth;
        }
    };

    private setContainerRef = (ref: HTMLDivElement) => {
        this.containerRef = ref;
        this.onResize();
    };

    private revealThreadId = (threadId: number | undefined) => {
        if (this.diffIntegration && threadId) {
            this.diffIntegration.revealThreadId(threadId);
        }
    };
}

interface IChangesWithDiscussionProps<TVersionDescriptor> {
    discussionService?: IDiscussionIntegrationService;
    filesService: IChangesFilesService<TVersionDescriptor>;
    paths: string[];
    viewerType: "summary" | "monaco";
    renderChanges(diffIntegrations?: IDiffIntegration[]): JSX.Element;
    getCodeSelectionLinkUrl: (selection: IEditorSelection, editorType?: number, diffOrientation?: DiffViewerOrientation) => string;
}

/**
 * This component makes sure that monaco/diff-summary integrations can subscribe/unsubscribe to thread changes
 * appropriately so that only the widgets get updated when threads change (instead of whole change viewer update)
 */
function ChangesWithDiscussion<TVersionDescriptor>(props: IChangesWithDiscussionProps<TVersionDescriptor>): JSX.Element {
    const { discussionService, filesService, paths, renderChanges, viewerType, getCodeSelectionLinkUrl } = props;
    let pendingWork: IPerformanceScenarioPendingWork | undefined;

    let diffIntegrations: IDiffIntegration[] | undefined;
    if (discussionService) {
        if (viewerType === "monaco") {
            diffIntegrations = [discussionService.getMonacoIntegration(paths[0], filesService.getDiffOrientation(DiffViewerType.detail), getCodeSelectionLinkUrl)];
        } else {
            diffIntegrations = discussionService.getDiffSummaryIntegrations(paths, filesService.getDiffOrientation(DiffViewerType.summary));
        }
    }

    React.useEffect(() => {
        // Track the performance of rendering the changes viewer
        if (pendingWork) {
            pendingWork.complete();
        }

        return () => {
            if (diffIntegrations) {
                diffIntegrations.forEach(d => d.dispose());
            }
        };
    });

    return (
        <PerformanceScenarioContext.Consumer>
            {scenarioProps => {
                pendingWork = scenarioProps.registerPendingWork("pr-render-tab");
                return renderChanges(diffIntegrations);
            }}
        </PerformanceScenarioContext.Consumer>
    );
}
